import { getEntityByKeyList } from "./index";

/**
 * Helper function to get multi-block group by block_id
 * @param {Array} sections - Array of form sections
 * @param {string} block_id - Block ID to search for
 * @returns {Array} Array of blocks with the same block_id, in their natural order
 */
export const getMultiBlockGroup = (sections, block_id) => {
  const allBlocks = [];
  
  const findBlocksRecursively = (entities, sectionIndex, entityPath = []) => {
    entities.forEach((entity, entityIndex) => {
      if (entity.form_entity_type === 'BLOCK' && entity.block_id === block_id) {
        allBlocks.push({
          ...entity,
          _debug_path: `section[${sectionIndex}].entities[${entityPath.join('].entities[')}${entityPath.length > 0 ? '].entities[' : ''}${entityIndex}]`
        });
      }
      if (entity.entities) {
        findBlocksRecursively(entity.entities, sectionIndex, [...entityPath, entityIndex]);
      }
    });
  };
  
  sections.forEach((section, sectionIndex) => {
    if (section.entities) {
      findBlocksRecursively(section.entities, sectionIndex);
    }
  });
  
  // Return blocks in their natural order as they appear in the structure
  return allBlocks;
};

// Helper function to get the index of a block within its multi-block group
export const getBlockIndexInGroup = (sections, block) => {
  const group = getMultiBlockGroup(sections, block.block_id);
  return group.findIndex(b => b.block_fe_id === block.block_fe_id);
};

export const getLinkedBlock = (block, {
  sections = [],
  // linked_blocks = [], // TODO check if we need this
}) => {
  // Check if this block has use_value_from property
  if (block?.use_value_from) {
    // This block directly has a link
    const linkedBlock = getEntityByKeyList(sections, {
      block_fe_id: block.use_value_from,
    });
    return linkedBlock || null;
  }
  
  // Check if this block is part of a multi-block group where the first block has use_value_from
  const multiBlockGroup = getMultiBlockGroup(sections, block.block_id);
  const firstBlock = multiBlockGroup[0];
  
  if (firstBlock && firstBlock.use_value_from && firstBlock.block_fe_id !== block.block_fe_id) {
    // This block is part of a multi-block group where the first block has a link
    const currentBlockIndex = getBlockIndexInGroup(sections, block);
    
    // Find the source block by block_fe_id
    const sourceFirstBlock = getEntityByKeyList(sections, {
      block_fe_id: firstBlock.use_value_from,
    });
    
    if (sourceFirstBlock) {
      const sourceGroup = getMultiBlockGroup(sections, sourceFirstBlock.block_id);
      // Link by index: current block index should match with source block index
      return sourceGroup[currentBlockIndex] || null;
    }
  }
  
  return null;
}

/**
 * Helper function to check if a block belongs to a specific group
 * @param {Object} block - Block to check
 * @param {string} group_block_id - Group block ID to check against
 * @returns {boolean} True if block belongs to the group
 */
export const isBlockInGroup = (block, group_block_id) => {
  return block.block_id === group_block_id;
};

/**
 * Find the block that contains a given field
 * @param {Object} field - Field to search for
 * @param {Array} sections - Array of form sections
 * @returns {Object|null} Block containing the field or null if not found
 */
export const findContainingBlock = (field, sections) => {
  const foundBlocks = []; // Collect all blocks where the field is found
  
  const findBlock = (entities, path = '') => {
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      const currentPath = `${path}[${i}]`;
      
      if (entity.form_entity_type === 'BLOCK' && entity.entities) {
        // Check if this block contains the field
        const fieldFound = getEntityByKeyList(entity.entities, { field_fe_id: field.field_fe_id });
        if (fieldFound) {
          foundBlocks.push({ block: entity, path: currentPath });
        }
      }
      if (entity.entities) {
        findBlock(entity.entities, `${currentPath}.entities`);
      }
    }
  };

  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex];
    if (section.entities) {
      findBlock(section.entities, `section[${sectionIndex}].entities`);
    }
  }
  
  // Return the first found block (as before)
  return foundBlocks.length > 0 ? foundBlocks[0].block : null;
};

/**
 * Update fields in linked multi-blocks
 * @param {Object} sourceBlock - Source block that was changed
 * @param {Object} changedField - Field that was changed
 * @param {any} newValue - New value to set
 * @param {Array} sections - Array of form sections
 */
export const updateLinkedMultiBlockFields = (sourceBlock, changedField, newValue, sections) => {
  // Get the source block group and find the position of the source block
  const sourceBlockGroup = getMultiBlockGroup(sections, sourceBlock.block_id);
  
  // Find index by block_fe_id in the group (no sorting, natural order)
  const sourceBlockIndex = sourceBlockGroup.findIndex(b => b.block_fe_id === sourceBlock.block_fe_id);

  // Find all multi-block groups that are linked to the source block group
  const processedBlockIds = new Set(); // To avoid processing the same block group multiple times
  
  // Find all blocks
  const allBlocks = [];
  const collectBlocks = (entities) => {
    entities.forEach(entity => {
      if (entity.form_entity_type === 'BLOCK') {
        allBlocks.push(entity);
      }
      if (entity.entities) {
        collectBlocks(entity.entities);
      }
    });
  };

  sections.forEach(section => {
    if (section.entities) {
      collectBlocks(section.entities);
    }
  });

  // Group blocks by block_id to process multi-block groups
  const blockGroups = new Map();
  allBlocks.forEach(block => {
    if (!blockGroups.has(block.block_id)) {
      blockGroups.set(block.block_id, []);
    }
    blockGroups.get(block.block_id).push(block);
  });

  // Process each block group
  blockGroups.forEach((blocks, blockId) => {
    if (processedBlockIds.has(blockId) || blockId === sourceBlock.block_id) {
      return; // Skip already processed groups and source group
    }

    // Get blocks in natural order (no sorting)
    const targetBlockGroup = getMultiBlockGroup(sections, blockId);
    
    // Check if the FIRST block of this group is linked to the source block group
    // (use_value_from exists only on the first block of each group)
    const firstBlock = targetBlockGroup[0];
    const linkedBlock = getLinkedBlock(firstBlock, { sections });
    
    if (linkedBlock && isBlockInGroup(linkedBlock, sourceBlock.block_id)) {
      processedBlockIds.add(blockId);
      
      // Update the field in the block that corresponds to the source block index
      if (targetBlockGroup[sourceBlockIndex]) {
        const targetBlock = targetBlockGroup[sourceBlockIndex];
        
        const correspondingField = getEntityByKeyList(targetBlock.entities, {
          field_internal_name: changedField.field_internal_name,
          index: changedField.index
        });
        
        if (correspondingField) {
          correspondingField.value = newValue;
        }
      }
    }
  });
};

// we use fields from linked block as the source of values for nested fields in the same way as in the case of field.use_value_from:
export const getLinkedBlockField = (field, block, {
  sections = [],
  linked_blocks = [], // TODO check if we need this
}) => {
  const linkedBlock = getLinkedBlock(block, { sections, linked_blocks });
  if (!linkedBlock) return null;

  const linkedBlockField = getEntityByKeyList(linkedBlock.entities, { field_internal_name: field.field_internal_name, index: field.index });

  return linkedBlockField || null;
}
